import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

/**
 * Contact Page
 * Simple contact information page
 */
const ContactPage = () => {
  const { i18n } = useTranslation()
  const isGerman = i18n.language === 'de'

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-gray-600 hover:text-purple-600 transition-colors mb-4"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {isGerman ? 'Zurück' : 'Back'}
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            {isGerman ? 'Kontakt' : 'Contact'}
          </h1>
          <p className="mt-2 text-gray-600">
            {isGerman
              ? 'Nehmen Sie Kontakt mit uns auf'
              : 'Get in touch with us'}
          </p>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-8 space-y-8">

            {/* Company Information */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? 'Emporion Innovations GmbH' : 'Emporion Innovations GmbH'}
              </h2>
              <div className="space-y-4">
                {/* Address */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {isGerman ? 'Adresse' : 'Address'}
                  </h3>
                  <p className="text-gray-700 ml-7">
                    Ludwigstr. 32<br/>
                    63067 Offenbach<br/>
                    {isGerman ? 'Deutschland' : 'Germany'}
                  </p>
                </div>

                {/* Email */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    E-Mail
                  </h3>
                  <p className="text-gray-700 ml-7">
                    <a href="mailto:info@emporion.technology" className="text-purple-600 hover:text-purple-700">
                      info@emporion.technology
                    </a>
                  </p>
                </div>

                {/* Phone */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    {isGerman ? 'Telefon' : 'Phone'}
                  </h3>
                  <p className="text-gray-700 ml-7">
                    <a href="tel:+4906999998088" className="text-purple-600 hover:text-purple-700">
                      +49 (0) 69 99998088
                    </a>
                  </p>
                </div>
              </div>
            </section>

            {/* Support Information */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? 'Support' : 'Support'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>
                  {isGerman
                    ? 'Für technischen Support oder Fragen zu Ihrem Account senden Sie uns bitte eine E-Mail an:'
                    : 'For technical support or questions about your account, please send us an email at:'}
                </p>
                <p className="font-medium">
                  <a href="mailto:info@emporion.technology" className="text-purple-600 hover:text-purple-700">
                    info@emporion.technology
                  </a>
                </p>
                <p className="mt-4">
                  {isGerman
                    ? 'Wir bemühen uns, Ihre Anfrage innerhalb von 24-48 Stunden zu beantworten.'
                    : 'We strive to respond to your inquiry within 24-48 hours.'}
                </p>
              </div>
            </section>

            {/* Business Hours */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? 'Geschäftszeiten' : 'Business Hours'}
              </h2>
              <div className="space-y-2 text-gray-700 text-sm">
                <p>
                  {isGerman ? 'Montag - Freitag: 9:00 - 17:00 Uhr (MEZ)' : 'Monday - Friday: 9:00 AM - 5:00 PM (CET)'}
                </p>
                <p className="text-gray-600 italic">
                  {isGerman
                    ? 'E-Mail-Anfragen werden auch außerhalb der Geschäftszeiten bearbeitet.'
                    : 'Email inquiries are also processed outside business hours.'}
                </p>
              </div>
            </section>

            {/* Legal Information */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? 'Rechtliche Informationen' : 'Legal Information'}
              </h2>
              <div className="space-y-2 text-gray-700 text-sm">
                <p>
                  {isGerman ? 'Geschäftsführer: Alexander Kuchta, Dennis Schmitz' : 'Managing Directors: Alexander Kuchta, Dennis Schmitz'}
                </p>
                <p>
                  {isGerman ? 'Registergericht: Offenbach am Main' : 'Registry Court: Offenbach am Main'}
                </p>
                <p>
                  {isGerman ? 'Registernummer: HRB 51283' : 'Registration Number: HRB 51283'}
                </p>
                <p>
                  {isGerman ? 'USt-IdNr.: DE284997944' : 'VAT ID: DE284997944'}
                </p>
              </div>
            </section>

          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center">
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-600">
            <Link to={isGerman ? '/impressum' : '/imprint'} className="hover:text-purple-600">
              {isGerman ? 'Impressum' : 'Imprint'}
            </Link>
            <Link to={isGerman ? '/datenschutz' : '/privacy'} className="hover:text-purple-600">
              {isGerman ? 'Datenschutz' : 'Privacy Policy'}
            </Link>
            <Link to="/terms" className="hover:text-purple-600">
              {isGerman ? 'AGB' : 'Terms of Service'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ContactPage
